import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

const base_url = process.env.REACT_APP_API_URL;

export default function UserProfile() {
  const { phone } = useParams();
  const token = localStorage.getItem("admin_token");

  const [user, setUser] = useState(null);
  const [total_referral, setTotalReferral] = useState(0);
  const [records, setRecords] = useState([]);

  const [password, setPassword] = useState("");
  const [referrer, setReferrer] = useState("");

  const [category, setCategory] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const limit = 20;

  /* -------- FETCH USER + RECORDS -------- */

  const fetchUser = async (p = 1) => {
    if (loading) return;

    try {
      setLoading(true);

      const res = await axios.get(
        `${base_url}/api/admin/user-details/${phone}?page=${p}&limit=${limit}&category=${category}&fromDate=${fromDate}&toDate=${toDate}`,
        {
          headers: { Authorization: token },
        },
      );

      if (res.data.success) {
        if (p === 1) {
          setUser(res.data.user);
          setRecords(res.data.records);
          setReferrer(res.data.user.invite);
          setTotalReferral(res.data.user.total_referral);
        } else {
          setRecords((prev) => [...prev, ...res.data.records]);
        }

        setPage(p);
        setHasMore(res.data.records.length === limit);
      }
    } catch {
      toast.error("Failed to load data");
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchUser(1);
  }, []);

  /* -------- SCROLL PAGINATION -------- */

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100
      ) {
        fetchUser(page + 1);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [page, hasMore]);

  /* -------- FILTER -------- */

  const applyFilter = () => {
    setPage(1);
    setHasMore(true);
    setRecords([]);
    fetchUser(1);
  };

  /* -------- UPDATE PASSWORD -------- */

  const updatePassword = async () => {
    try {
      await axios.post(
        `${base_url}/api/admin/update-password`,
        { phone, password },
        { headers: { Authorization: token } },
      );

      toast.success("Password updated");
      setPassword("");
    } catch {
      toast.error("Update failed");
    }
  };

  /* -------- UPDATE REFERRAL -------- */

  const updateReferrer = async () => {
    try {
      await axios.post(
        `${base_url}/api/admin/update-referrer`,
        { phone, invite: referrer },
        { headers: { Authorization: token } },
      );

      toast.success("Referrer updated");
    } catch {
      toast.error("Update failed");
    }
  };

  if (!user) return <div className="p-4">Loading...</div>;

  return (
    <div className="container-fluid p-4">
      <h3 className="mb-4">User Details</h3>

      {/* USER INFO */}

      <div className="row mb-4">
        <div className="col-md-6">
          <ul className="list-unstyled">
            <li>
              <b>User ID :</b> {user.id_user}
            </li>
            <li>
              <b>IP Address :</b> {user.ip_address}
            </li>

            <li>
              <b>User Password :</b>

              <div className="d-flex mt-1">
                <input
                  className="form-control me-2"
                  placeholder="Enter Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />

                <button className="btn btn-primary" onClick={updatePassword}>
                  Update
                </button>
              </div>
            </li>
          </ul>
        </div>

        <div className="col-md-6">
          <ul className="list-unstyled">
            <li>
              <b>Phone :</b> {user.phone}
            </li>

            <li>
              <b>Created At :</b> {new Date(user.created_at).toLocaleString()}
            </li>

            <li>
              <b>Referred By :</b>

              <div className="d-flex mt-1">
                <input
                  className="form-control me-2"
                  value={referrer}
                  onChange={(e) => setReferrer(e.target.value)}
                />

                <button className="btn btn-primary" onClick={updateReferrer}>
                  Update
                </button>
              </div>
            </li>
          </ul>
        </div>
      </div>

      {/* STATS */}

      <div className="row text-center mb-4">
        <Stat title="Balance" value={`₹${parseFloat(user.money).toFixed(2)}`} />
        <Stat title="Total Referral" value={total_referral} />
        <Stat title="Total Recharge" value={`₹${user.total_recharge}`} />
        <Stat title="Total Withdraw" value={`₹${user.total_withdraw}`} />
        <Stat title="Total Bonus" value={`₹${user.total_bonus}`} />
        <Stat title="Total Commission" value={`₹${user.total_commission}`} />
        <Stat title="Total Bet" value={`₹${user.total_bet}`} />
        <Stat title="Total Profit" value={`₹${user.total_profit}`} />
      </div>

      {/* FILTERS */}

      <div className="row g-2 mb-3">
        <div className="col-md-2">
          <input
            type="date"
            className="form-control"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>

        <div className="col-md-2">
          <input
            type="date"
            className="form-control"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>

        <div className="col-md-2">
          <select
            className="form-control"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">All</option>
            <option value="deposit">Deposit</option>
            <option value="withdraw">Withdraw</option>
            {/* <option value="aviator">Aviator</option> */}
            <option value="matka">Matka</option>
            <option value="lucky7">Lucky7</option>
            <option value="12 Tiles">12 Tiles</option>
            <option value="Mine game">Mine game</option>
            <option value="Win Go">Win Go</option>
            <option value="Spin Wheel">Spin Wheel</option>
          </select>
        </div>

        <div className="col-md-2">
          <button className="btn btn-primary w-100" onClick={applyFilter}>
            Apply
          </button>
        </div>
      </div>

      {/* TABLE */}

      <div className="card p-2">
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>#</th>
                <th>Category</th>
                <th>Amount</th>
                <th>Balance</th>
                <th>Status</th>
                <th>Type</th>
                <th>Time</th>
                <th>Remark</th>
              </tr>
            </thead>

            <tbody>
              {records.map((r, i) => (
                <tr key={r.id}>
                  <td>{i + 1}</td>

                  <td>
                    {r.category.charAt(0).toUpperCase() + r.category.slice(1)}
                  </td>

                  <td
                    className={r.status === 1 ? "text-success" : "text-danger"}
                  >
                    {r.amount > 0 ? `+₹${r.amount}` : `-₹${Math.abs(r.amount)}`}
                  </td>

                  <td>₹{r.current_money}</td>

                  <td>
                    {r.status === 1 ? (
                      <span className="badge bg-success">Success</span>
                    ) : (
                      <span className="badge bg-danger">Failed</span>
                    )}
                  </td>

                  <td>
                    {r.type === "credit" || r.type === "refund" ? (
                      <span className="badge bg-success">
                        {r.type.charAt(0).toUpperCase() + r.type.slice(1)}
                      </span>
                    ) : (
                      <span className="badge bg-danger">
                        {r.type.charAt(0).toUpperCase() + r.type.slice(1)}
                      </span>
                    )}
                  </td>

                  <td>{new Date(r.created_at).toLocaleString()}</td>
                  <td>{r.remark.charAt(0).toUpperCase() + r.remark.slice(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {loading && (
            <div className="text-center p-3">Loading more records...</div>
          )}

          {!hasMore && (
            <div className="text-center p-3 text-muted">No more records</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ title, value }) {
  return (
    <div className="col-6 col-md-3 mb-lg-3">
      <div className="card p-3">
        <div className="text-muted">{title}</div>

        <h4>{value}</h4>
      </div>
    </div>
  );
}
